import { doc, getFirestore, onSnapshot } from "@firebase/firestore";
import { Skeleton } from "@mui/material";
import { Field, Form, Formik } from "formik";
import { post } from "jsx/api";
import SecondaryDocumentRow from "jsx/element/secondary-document-row";
import Header4 from "jsx/layout/header4";
import React, { useEffect, useState } from "react";

import SigningSection from "./secondaries-signing-section";
import WaitingForSignatures from "./secondaries-waiting-for-secondaries";

function CompanySecondariesSignCompany({ match }) {
  const [document, setDocument] = useState();
  const [transaction, setTransaction] = useState();

  const { organizationId, secondaryId, transactionId, documentId } =
    match.params;

  const db = getFirestore();

  async function subscribeToTransaction(
    organizationId,
    secondaryId,
    transactionId
  ) {
    onSnapshot(
      doc(
        db,
        "organizations",
        organizationId,
        "organizations_admin",
        "data",
        "secondaries",
        secondaryId,
        "secondary_transactions",
        transactionId
      ),
      (doc) => {
        const transactionData = doc.data();
        setTransaction(transactionData);
      }
    );
  }

  async function subscribeToTransferDoc(
    organizationId,
    secondaryId,
    transactionId,
    documentId
  ) {
    onSnapshot(
      doc(
        db,
        "organizations",
        organizationId,
        "organizations_admin",
        "data",
        "secondaries",
        secondaryId,
        "secondary_transactions",
        transactionId,
        "documents",
        documentId
      ),
      (doc) => {
        const docMetadata = doc.data();
        setDocument(docMetadata);
      }
    );
  }

  async function handleSubmit({
    signatureImage,
    signerName,
    signerTitle,
    isPreview,
  }) {
    const params = {
      signerParty: "company",
      organizationId: organizationId,
      secondaryId: secondaryId,
      transactionId: transactionId,
      signature: signatureImage,
      signerName: signerName,
      signerTitle: signerTitle,
      isPreview: isPreview,
      documentId,
    };

    const result = await post(`signTransferDoc`, params);
    if (isPreview) {
      const uri = "data:pdf;base64," + result.data;

      var downloadLink = window.document.createElement("a");
      downloadLink.href = uri;
      downloadLink.download = "DRAFT.docx";

      window.document.body.appendChild(downloadLink);
      downloadLink.click();
      window.document.body.removeChild(downloadLink);
      return;
    }
  }

  useEffect(() => {
    subscribeToTransferDoc(
      organizationId,
      secondaryId,
      transactionId,
      documentId
    );
    subscribeToTransaction(organizationId, secondaryId, transactionId);
  }, [organizationId, secondaryId, transactionId, documentId]);

  const isLoaded = transaction && document;

  const documentPreview = (
    <SecondaryDocumentRow
      organizationId={organizationId}
      secondaryId={secondaryId}
      transactionId={transactionId}
      documentId={documentId}
      showSigning={false}
    />
  );

  return (
    <>
      <Header4 hideAllMenuItems />

      <div className="content-body section-padding">
        <div className="container">
          <div className="row justify-content-center align-items-center">
            <div className="col-9">
              <div className="card outlined">
                {!isLoaded && (
                  <>
                    <Skeleton height={80} />
                    <Skeleton height={240} />
                    <Skeleton height={80} />
                    <Skeleton height={80} />
                    <Skeleton height={240} />
                    <Skeleton height={80} />
                    <Skeleton height={80} />
                  </>
                )}
                {isLoaded &&
                  (document.voided ? (
                    <Voided documentPreviewSection={documentPreview} />
                  ) : document.isCompleted ? (
                    <Completed documentPreviewSection={documentPreview} />
                  ) : document.isSignedByCompany ? (
                    <WaitingForSignatures
                      isWaiting={document.isSignedByCompany}
                      transaction={transaction}
                      documentPreviewSection={documentPreview}
                    />
                  ) : (
                    <SigningForm
                      transaction={transaction}
                      initialValues={
                        // TODO: Pre-fill values with stored data
                        {
                          signerName: "",
                          signerTitle: "",
                        }
                      }
                      documentPreviewSection={documentPreview}
                      onSubmit={handleSubmit}
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Completed({ documentPreviewSection }) {
  return (
    <>
      <div className="card-header">
        <h4 className="card-title mb-3">Completed</h4>
      </div>
      <div className="card-body">
        {/* TODO: Add copy */}
        {documentPreviewSection}
      </div>
    </>
  );
}

function Voided({ documentPreviewSection }) {
  return (
    <>
      <div className="card-header">
        <h4 className="card-title">This document has been voided</h4>
      </div>
      <div className="card-body">
        <p className="mb-2">
          This document has been voided and is no longer available for signing
        </p>
        {documentPreviewSection}
      </div>
    </>
  );
}

function SigningForm({
  transaction,
  initialValues,
  documentPreviewSection,
  onSubmit,
}) {
  const [signatureImage, setSignatureImage] = useState();
  const [isError, setError] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSigning, setIsSigning] = useState(false);

  const pricePerShare =
    transaction.pricePerShareInBaseUnits / transaction.pricePerSharePrecision;
  const pricePerShareFormat = {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Math.max(
      2,
      Math.log10(transaction.pricePerSharePrecision)
    ),
  };

  const totalAmount = pricePerShare * transaction.quantity;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values) => {
        const { signerName, signerTitle } = values;
        if (!(signatureImage && signerName && signerTitle)) {
          setError(true);
          return;
        }

        if (values.isPreview) {
          setIsPreviewing(true);
        } else {
          setIsSigning(true);
        }

        await onSubmit({
          signatureImage: signatureImage,
          signerName: signerName,
          signerTitle: signerTitle,
          isPreview: values.isPreview,
        });
        setIsPreviewing(false);
        setIsSigning(false);
      }}
    >
      {({ values, isSubmitting, submitForm, setFieldValue }) => (
        <Form>
          <div className="card-header" style={{ display: "block" }}>
            <h2>Sign Documents</h2>
            <p>Sign documents to formally accept this proposed transfer.</p>
          </div>
          <div className="card-body">
            <div className="row">
              {/* Review section */}
              <h4 className="form-section-header mt-0">Review terms</h4>
              <h6 className="form-label">
                I acknowledge this stock transfer at the terms below:
              </h6>
              <ul className="investment-overview ms-4 mt-1">
                {transaction && (
                  <>
                    <li>
                      <strong>Certificate Number </strong>
                      {transaction.securityDetails.certificateNo}
                    </li>
                    <li>
                      <strong>Company </strong>
                      {transaction.companyName}
                    </li>
                    <li>
                      <strong>Seller </strong>
                      {transaction.sellerName}
                    </li>
                    <li>
                      <strong>Buyer </strong>
                      {transaction.buyerName}
                    </li>
                    <li>
                      <strong>Share class </strong>
                      {
                        // TODO: grab this from the transaction once it gets added
                        "Common Stock"
                      }
                    </li>
                    <li>
                      <strong>Number of shares </strong>
                      {transaction.quantity.toLocaleString()}
                    </li>
                    <li>
                      <strong>Price per share </strong>
                      {pricePerShare.toLocaleString(
                        undefined,
                        pricePerShareFormat
                      )}
                    </li>

                    <li>
                      <strong>Total amount </strong>
                      {totalAmount.toLocaleString(undefined, {
                        style: "currency",
                        currency: "USD",
                      })}
                    </li>
                  </>
                )}
              </ul>

              {/* Signature block section */}
              <h4 className="form-section-header mt-4">Signature Block</h4>

              <div className="mb-0 col-xl-12">
                <h6 className="form-label">What is your full name?</h6>
                <Field
                  type="text"
                  name="signerName"
                  className={
                    "form-control " +
                    (isError && !values.signerName && "is-invalid")
                  }
                  placeholder="Firstname Lastname"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </div>
              <div className="mb-0 col-xl-12">
                <h6 className="form-label">What is your title?</h6>
                <Field
                  type="text"
                  name="signerTitle"
                  className={
                    "form-control " +
                    (isError && !values.signerTitle && "is-invalid")
                  }
                  placeholder="CEO"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </div>

              {/* Document preview section */}
              <SigningSection
                documentPreviewSection={documentPreviewSection}
                isError={isError}
                signatureImage={signatureImage}
                setSignatureImage={setSignatureImage}
                isSigning={isSigning}
                isPreviewing={isPreviewing}
                submitForm={() => {
                  setFieldValue("isPreview", false);
                  submitForm();
                }}
                downloadPreview={() => {
                  setFieldValue("isPreview", true);
                  submitForm();
                }}
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default CompanySecondariesSignCompany;
